/**
 * Výchozí dopravní režimy a tovární funkce pro vlastní režimy.
 * Hodnoty jsou transparentní odhady (rychlosti, ceny, emise) pro ČR;
 * uživatel je může upravit v nastavení režimů.
 */
import type { ModeProfile } from './types';

let customCounter = 0;

export function defaultModes(): ModeProfile[] {
  return [
    {
      id: 'walk',
      kind: 'walk',
      name: 'Pěšky',
      icon: 'walk',
      effort: 3.5,
      color: '#22a06b',
      enabled: true,
      speedKmh: 4.8,
      circuity: 1.25,
      setupMin: 0,
      teardownMin: 0,
      waitMin: 0,
      costFixed: 0,
      costPerKm: 0,
      emissionsPerKm: 0,
      comfort: 3,
      reliability: 0.95,
      accessible: true,
      carFree: true,
      maxDistanceKm: 12,
      routingProfile: 'foot',
    },
    {
      id: 'run',
      kind: 'walk',
      name: 'Během',
      icon: 'run',
      effort: 5,
      color: '#0fa3a3',
      enabled: false,
      speedKmh: 9.5,
      circuity: 1.25,
      setupMin: 2,
      teardownMin: 3,
      waitMin: 0,
      costFixed: 0,
      costPerKm: 0,
      emissionsPerKm: 0,
      comfort: 2,
      reliability: 0.9,
      accessible: false,
      carFree: true,
      maxDistanceKm: 18,
      routingProfile: 'foot',
    },
    {
      id: 'bike',
      kind: 'bike',
      name: 'Kolo',
      icon: 'bike',
      effort: 3,
      color: '#e07b16',
      enabled: true,
      speedKmh: 15,
      circuity: 1.3,
      setupMin: 2,
      teardownMin: 2,
      waitMin: 0,
      costFixed: 0,
      costPerKm: 0.3,
      emissionsPerKm: 5,
      comfort: 3,
      reliability: 0.9,
      accessible: false,
      carFree: true,
      maxDistanceKm: 40,
      routingProfile: 'bike',
    },
    {
      id: 'ebike',
      kind: 'bike',
      name: 'Elektrokolo',
      icon: 'ebike',
      effort: 2,
      color: '#c2701d',
      enabled: false,
      speedKmh: 21,
      circuity: 1.3,
      setupMin: 2,
      teardownMin: 2,
      waitMin: 0,
      costFixed: 0,
      costPerKm: 0.6,
      emissionsPerKm: 8,
      comfort: 4,
      reliability: 0.9,
      accessible: false,
      carFree: true,
      maxDistanceKm: 60,
      routingProfile: 'bike',
    },
    {
      id: 'scooter',
      kind: 'micro',
      name: 'Sdílená koloběžka',
      icon: 'scooter',
      effort: 2,
      color: '#a855f7',
      enabled: false,
      speedKmh: 14,
      circuity: 1.3,
      setupMin: 1,
      teardownMin: 1,
      waitMin: 3,
      costFixed: 25,
      costPerKm: 8,
      emissionsPerKm: 25,
      comfort: 3,
      reliability: 0.75,
      accessible: false,
      carFree: true,
      maxDistanceKm: 15,
      routingProfile: 'bike',
    },
    {
      id: 'skate',
      kind: 'skate',
      name: 'Brusle',
      icon: 'skate',
      effort: 4.5,
      color: '#ec4899',
      enabled: false,
      speedKmh: 11,
      circuity: 1.4,
      setupMin: 4,
      teardownMin: 4,
      waitMin: 0,
      costFixed: 0,
      costPerKm: 0,
      emissionsPerKm: 0,
      comfort: 2,
      reliability: 0.7,
      accessible: false,
      carFree: true,
      maxDistanceKm: 20,
      routingProfile: 'bike',
    },
    {
      id: 'car',
      kind: 'car',
      name: 'Auto',
      icon: 'car',
      effort: 1,
      color: '#3b82f6',
      enabled: true,
      speedKmh: 34,
      circuity: 1.35,
      setupMin: 3,
      teardownMin: 6, // hledání parkování
      waitMin: 0,
      costFixed: 0,
      costPerKm: 7.5, // palivo + amortizace
      emissionsPerKm: 150,
      comfort: 5,
      reliability: 0.8,
      accessible: true,
      carFree: false,
      routingProfile: 'car',
    },
    {
      id: 'taxi',
      kind: 'car',
      name: 'Taxi / spolujízda',
      icon: 'taxi',
      effort: 1,
      color: '#eab308',
      enabled: false,
      speedKmh: 34,
      circuity: 1.35,
      setupMin: 0,
      teardownMin: 1,
      waitMin: 6,
      costFixed: 60,
      costPerKm: 28,
      emissionsPerKm: 150,
      comfort: 5,
      reliability: 0.8,
      accessible: true,
      carFree: true,
      routingProfile: 'car',
    },
    {
      id: 'pt',
      kind: 'pt',
      name: 'Veřejná doprava',
      icon: 'bus',
      effort: 1.5,
      color: '#d6293a',
      enabled: true,
      speedKmh: 25, // jen informativně; reálně dle linek
      circuity: 1.2,
      setupMin: 0,
      teardownMin: 0,
      waitMin: 0,
      costFixed: 0,
      costPerKm: 0,
      emissionsPerKm: 35,
      comfort: 3,
      reliability: 0.85,
      accessible: true,
      carFree: true,
      routingProfile: null,
    },
    {
      id: 'bike-pt',
      kind: 'combined',
      name: 'Kolo + MHD',
      icon: 'combined',
      effort: 2.5,
      color: '#8a4fff',
      enabled: false,
      speedKmh: 0,
      circuity: 1.25,
      setupMin: 0,
      teardownMin: 0,
      waitMin: 0,
      costFixed: 0,
      costPerKm: 0,
      emissionsPerKm: 0,
      comfort: 3,
      reliability: 0.8,
      accessible: false,
      carFree: true,
      routingProfile: null,
      accessModeId: 'bike',
    },
    {
      id: 'car-pt',
      kind: 'combined',
      name: 'P+R (auto + MHD)',
      icon: 'combined',
      effort: 1.5,
      color: '#0ea5e9',
      enabled: false,
      speedKmh: 0,
      circuity: 1.3,
      setupMin: 0,
      teardownMin: 0,
      waitMin: 0,
      costFixed: 20, // parkovné P+R
      costPerKm: 0,
      emissionsPerKm: 0,
      comfort: 4,
      reliability: 0.8,
      accessible: true,
      carFree: false,
      routingProfile: null,
      accessModeId: 'car',
    },
  ];
}

/** Nový uživatelský režim s rozumnými výchozími hodnotami. */
export function createCustomMode(partial?: Partial<ModeProfile>): ModeProfile {
  customCounter += 1;
  return {
    id: `custom-${Date.now().toString(36)}-${customCounter}`,
    kind: 'custom',
    name: 'Můj režim',
    icon: 'custom',
    effort: 3,
    color: '#64748b',
    enabled: true,
    speedKmh: 10,
    circuity: 1.3,
    setupMin: 0,
    teardownMin: 0,
    waitMin: 0,
    costFixed: 0,
    costPerKm: 0,
    emissionsPerKm: 0,
    comfort: 3,
    reliability: 0.8,
    accessible: false,
    carFree: true,
    routingProfile: null,
    userDefined: true,
    ...partial,
  };
}

/** Oblasti dopravních režimů — seskupení pro UI (panel, zóny). */
export interface ModeGroup {
  id: string;
  name: string;
  kinds: ModeProfile['kind'][];
}

export const MODE_GROUPS: ModeGroup[] = [
  { id: 'foot', name: 'Pěší', kinds: ['walk'] },
  { id: 'micro', name: 'Kolo & mikromobilita', kinds: ['bike', 'micro', 'skate'] },
  { id: 'motor', name: 'Motorová', kinds: ['car'] },
  { id: 'pt', name: 'Veřejná doprava', kinds: ['pt'] },
  { id: 'combo', name: 'Kombinace', kinds: ['combined'] },
  { id: 'custom', name: 'Vlastní', kinds: ['custom'] },
];

/** Rozdělí režimy do oblastí (při zachování pořadí uvnitř skupin). */
export function groupModes(modes: ModeProfile[]): Array<{ group: ModeGroup; modes: ModeProfile[] }> {
  return MODE_GROUPS.map((group) => ({
    group,
    modes: modes.filter((m) => group.kinds.includes(m.kind)),
  })).filter((g) => g.modes.length > 0);
}

/** Najde režim podle id (vč. fallbacku pro robustnost). */
export function findMode(modes: ModeProfile[], id: string): ModeProfile | undefined {
  return modes.find((m) => m.id === id);
}

/**
 * Popis zdroje dat režimu — odkud bere trasu, čas, vzdálenost a omezení.
 * Zobrazuje se v editoru režimu a detailu trasy.
 */
export function modeDataSourceNote(mode: ModeProfile): string {
  if (mode.kind === 'pt') {
    return 'Jízdní řády: GTFS import nebo ručně zadané linky (reálná data); demo síť je jen označený odhad. Čas = docházka + čekání dle řádu + jízda + přestupy. Geometrie mezi zastávkami je modelová (spojnice zastávek).';
  }
  if (mode.kind === 'combined') {
    return 'Kombinace: první úsek jede návazným režimem (jeho zdroj dat), zbytek dle jízdních řádů MHD. Vozidlo zůstává u nástupní zastávky.';
  }
  if (mode.routingProfile === 'car') {
    return 'Trasa a délka: routovací služba (OSRM/ORS, data OpenStreetMap), profil auto. Čas jízdy přebíráme ze služby a násobíme modelem špičky; bez služby platí průměrná rychlost × reálná délka. Fallback bez sítě: vzdušná čára × nepřímost — vždy označeno „Odhad".';
  }
  if (mode.routingProfile) {
    const profile = mode.routingProfile === 'bike' ? 'kolo' : 'pěší';
    return `Trasa a délka: routovací služba (OSRM/ORS, data OpenStreetMap), profil ${profile}. Čas = reálná délka / vaše průměrná rychlost (tempo je osobní). Fallback bez sítě: vzdušná čára × nepřímost — vždy označeno „Odhad".`;
  }
  return 'Režim bez routovacího profilu: vzdálenost = vzdušná čára × koeficient nepřímosti, čas = vzdálenost / rychlost. Výsledek je vždy označen jako odhad.';
}
